import { enqueueSnackbar } from 'notistack';
import { MutableRefObject, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AppFileRouterEndpoint } from 'src/interfaces/AppFileRouter';
import { UploadThingResponse } from 'src/interfaces/FileManager';
import {
  compressImagesForUpload,
  ImageCompressPreset,
} from 'src/helpers/compressImageForUpload';
import { useUploadThing } from 'src/helpers/uploadThing';
import useFileManagerStore from 'src/stores/fileManagerStore';

type UploadOptions = {
  compress?: ImageCompressPreset | false;
};

type PendingUpload = {
  resolve: (res: UploadThingResponse[]) => void;
  reject: (err: Error) => void;
};

function useEndpointUploader(
  endpoint: AppFileRouterEndpoint,
  pendingRef: MutableRefObject<Map<AppFileRouterEndpoint, PendingUpload>>,
) {
  return useUploadThing(endpoint, {
    onClientUploadComplete: res => {
      const pending = pendingRef.current.get(endpoint);
      pending?.resolve(res ?? []);
      pendingRef.current.delete(endpoint);
    },
    onUploadError: error => {
      const pending = pendingRef.current.get(endpoint);
      pending?.reject(error instanceof Error ? error : new Error(String(error)));
      pendingRef.current.delete(endpoint);
    },
  });
}

export function useFileUploadBatch() {
  const { t } = useTranslation();
  const fetchFiles = useFileManagerStore(state => state.fetchFiles);
  const createFileFromUpload = useFileManagerStore(state => state.createFileFromUpload);
  const [isUploading, setIsUploading] = useState(false);
  const pendingResolversRef = useRef<Map<AppFileRouterEndpoint, PendingUpload>>(new Map());

  const persistUploadResults = useCallback(
    async (results: UploadThingResponse[], endpoint: AppFileRouterEndpoint) => {
      if (results.length === 0) {
        return;
      }

      const createPromises = results.map(file =>
        createFileFromUpload({
          fileName: file.name,
          fileUrl: file.url,
          fileKey: file.key,
          fileSize: file.size,
          uploadEndpoint: endpoint,
        }),
      );
      await Promise.all(createPromises);
    },
    [createFileFromUpload],
  );

  const imageUploader = useEndpointUploader('image', pendingResolversRef);
  const documentUploader = useEndpointUploader('document', pendingResolversRef);
  const trackMapUploader = useEndpointUploader('trackMap', pendingResolversRef);

  const uploadersRef = useRef({
    image: imageUploader,
    document: documentUploader,
    trackMap: trackMapUploader,
  });
  uploadersRef.current = {
    image: imageUploader,
    document: documentUploader,
    trackMap: trackMapUploader,
  };

  const uploadToEndpoint = useCallback(
    async (
      files: File[],
      endpoint: AppFileRouterEndpoint,
      options: UploadOptions = {},
    ) => {
      const compress = options.compress ?? (endpoint === 'image' ? 'default' : false);
      const payload =
        compress === false
          ? files
          : endpoint === 'image'
            ? await compressImagesForUpload(files, compress)
            : files;

      const uploadPromise = new Promise<UploadThingResponse[]>((resolve, reject) => {
        pendingResolversRef.current.set(endpoint, { resolve, reject });
      });

      await uploadersRef.current[endpoint].startUpload(payload);
      const results = await uploadPromise;
      await persistUploadResults(results, endpoint);
      return results;
    },
    [persistUploadResults],
  );

  const uploadFiles = useCallback(
    async (files: File[], endpoint: AppFileRouterEndpoint) => {
      if (files.length === 0) {
        return;
      }

      setIsUploading(true);
      try {
        await uploadToEndpoint(files, endpoint);
        enqueueSnackbar(t('form.upload.success'), { variant: 'success' });
        fetchFiles();
      } catch {
        enqueueSnackbar(t('form.upload.error'), { variant: 'error' });
      } finally {
        setIsUploading(false);
      }
    },
    [fetchFiles, t, uploadToEndpoint],
  );

  const uploadFilesGrouped = useCallback(
    async (groups: Map<AppFileRouterEndpoint, File[]>) => {
      if (groups.size === 0) {
        return;
      }

      setIsUploading(true);
      try {
        for (const [endpoint, groupFiles] of groups) {
          await uploadToEndpoint(groupFiles, endpoint);
        }
        enqueueSnackbar(t('form.upload.success'), { variant: 'success' });
        fetchFiles();
      } catch {
        enqueueSnackbar(t('form.upload.error'), { variant: 'error' });
      } finally {
        setIsUploading(false);
      }
    },
    [fetchFiles, t, uploadToEndpoint],
  );

  return {
    isUploading,
    uploadFiles,
    uploadFilesGrouped,
    uploadToEndpoint,
  };
}
